import React, { useState, useEffect, useRef } from 'react'
import * as api from '../../services/api.js'

const NOTE_COLORS = [
  { label: 'Yellow',   value: '#fef9c3' },
  { label: 'Pink',     value: '#fde8e8' },
  { label: 'Sage',     value: '#e8f5ed' },
  { label: 'Lavender', value: '#ede8f5' },
]

function timeAgo(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  if (isNaN(d)) return ''
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// ── CommentCard ────────────────────────────────────────────────────────────────

function CommentCard({ comment, onEdit, onResolve, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [body,    setBody]    = useState(comment.body)

  async function saveEdit() {
    await api.updateWritingComment(comment.id, { body })
    onEdit({ ...comment, body })
    setEditing(false)
  }

  return (
    <div
      className="comment-card"
      style={{ borderLeftColor: comment.color || '#fef9c3', opacity: comment.resolved ? 0.5 : 1 }}
    >
      {comment.quoted_text && (
        <div className="comment-quote">{comment.quoted_text}</div>
      )}

      {editing ? (
        <div>
          <textarea
            className="comment-edit-ta"
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={3}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <button className="comment-action-btn" onClick={saveEdit}>Save</button>
            <button className="comment-action-btn muted" onClick={() => { setEditing(false); setBody(comment.body) }}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {comment.body && <div className="comment-body">{comment.body}</div>}
          {comment.resolved && (
            <div style={{ fontSize: '0.65rem', color: 'var(--sage)', fontWeight: 600, marginTop: 3 }}>Resolved ✓</div>
          )}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: '0.65rem', color: 'var(--stone)' }}>{timeAgo(comment.created_at)}</span>
        {!editing && (
          <div className="comment-actions">
            <button className="comment-icon-btn" title="Edit"    onClick={() => setEditing(true)}>✎</button>
            <button className="comment-icon-btn" title={comment.resolved ? 'Unresolve' : 'Resolve'} onClick={() => onResolve(comment)}>
              {comment.resolved ? '↩' : '✓'}
            </button>
            <button className="comment-icon-btn" title="Delete"  onClick={() => onDelete(comment.id)}>✕</button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── AddNoteForm ────────────────────────────────────────────────────────────────

function AddNoteForm({ projectId, chapId, quotedText, onSaved, onCancel }) {
  const [body,  setBody]  = useState('')
  const [color, setColor] = useState('#fef9c3')
  const textRef = useRef(null)

  useEffect(() => { setTimeout(() => textRef.current?.focus(), 50) }, [])

  async function handleSave() {
    if (!body.trim() && !quotedText) return
    const comment = {
      project_id:   projectId,
      chapter_id:   chapId !== 'general' ? chapId : null,
      quoted_text:  quotedText || null,
      body:         body.trim(),
      color,
    }
    const res = await api.saveWritingComment(comment)
    onSaved({ ...comment, id: res?.id || Date.now(), created_at: new Date().toISOString() })
    setBody('')
  }

  return (
    <div className="add-note-form">
      {quotedText && (
        <div className="comment-quote" style={{ marginBottom: 6 }}>"{quotedText}"</div>
      )}
      <textarea
        ref={textRef}
        className="comment-edit-ta"
        rows={3}
        placeholder="Write a note..."
        value={body}
        onChange={e => setBody(e.target.value)}
      />
      <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
        {NOTE_COLORS.map(c => (
          <button
            key={c.value}
            title={c.label}
            onClick={() => setColor(c.value)}
            style={{
              width: 20, height: 20, borderRadius: '50%', background: c.value,
              border: color === c.value ? '2px solid var(--bark)' : '1px solid rgba(0,0,0,0.15)',
              cursor: 'pointer',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button className="comment-action-btn" onClick={handleSave} disabled={!body.trim() && !quotedText} style={{ opacity: body.trim() || quotedText ? 1 : 0.45 }}>
          Save
        </button>
        <button className="comment-action-btn muted" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

// ── WritingCommentsPanel ──────────────────────────────────────────────────────

export default function WritingCommentsPanel({
  pid,
  chapId = 'general',
  open,
  onToggle,
  pendingQuote,
  onClearPending,
}) {
  const [comments,     setComments]     = useState([])
  const [showAll,      setShowAll]      = useState(false)
  const [hideResolved, setHideResolved] = useState(true)
  const [adding,       setAdding]       = useState(false)
  const prevPendingRef = useRef(null)

  useEffect(() => {
    if (!pid) return
    loadComments()
  }, [pid, chapId, showAll])

  async function loadComments() {
    const data = await api.getWritingComments(
      showAll
        ? { projectId: pid }
        : { chapterId: chapId !== 'general' ? chapId : undefined, projectId: pid }
    )
    setComments(data || [])
  }

  // Open add form when pendingQuote arrives from selection toolbar
  useEffect(() => {
    if (pendingQuote && pendingQuote !== prevPendingRef.current) {
      prevPendingRef.current = pendingQuote
      setAdding(true)
    }
  }, [pendingQuote])

  function handleSaved(comment) {
    setComments(prev => [comment, ...prev])
    setAdding(false)
    onClearPending?.()
  }

  function handleEdit(updated) {
    setComments(prev => prev.map(c => c.id === updated.id ? updated : c))
  }

  async function handleResolve(comment) {
    const resolved = comment.resolved ? 0 : 1
    await api.updateWritingComment(comment.id, { resolved })
    setComments(prev => prev.map(c => c.id === comment.id ? { ...c, resolved } : c))
  }

  async function handleDelete(id) {
    await api.deleteWritingComment(id)
    setComments(prev => prev.filter(c => c.id !== id))
  }

  const visible = hideResolved ? comments.filter(c => !c.resolved) : comments
  const count   = comments.filter(c => !c.resolved).length

  // Collapsed tab
  if (!open) {
    return (
      <div className="write-comments-collapsed-tab" onClick={onToggle} title="Open notes & highlights">
        <span style={{ fontSize: '0.9rem' }}>💬</span>
        {count > 0 && <span style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--bark)' }}>{count}</span>}
      </div>
    )
  }

  return (
    <div className="write-comments-panel open">
      {/* Header */}
      <div className="comments-panel-header">
        <div>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--ink)' }}>Notes &amp; highlights</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--stone)' }}>
            {showAll ? 'All chapters' : 'This chapter'}
          </div>
        </div>
        <button className="panel-close-btn" onClick={onToggle} style={{ flexShrink: 0 }}>✕</button>
      </div>

      {/* Toggles */}
      <div style={{ display: 'flex', gap: 6, padding: '0 0.75rem 0.5rem', flexWrap: 'wrap' }}>
        <button
          className={`b-filter-chip${!showAll ? ' active' : ''}`}
          style={{ fontSize: '0.65rem' }}
          onClick={() => setShowAll(false)}
        >
          Chapter only
        </button>
        <button
          className={`b-filter-chip${showAll ? ' active' : ''}`}
          style={{ fontSize: '0.65rem' }}
          onClick={() => setShowAll(true)}
        >
          Show all
        </button>
        <button
          className={`b-filter-chip${hideResolved ? ' active' : ''}`}
          style={{ fontSize: '0.65rem', marginLeft: 'auto' }}
          onClick={() => setHideResolved(v => !v)}
        >
          {hideResolved ? 'Show resolved' : 'Hide resolved'}
        </button>
      </div>

      {/* Comment list */}
      <div className="comments-list">
        {adding && (
          <AddNoteForm
            projectId={pid}
            chapId={chapId}
            quotedText={pendingQuote}
            onSaved={handleSaved}
            onCancel={() => { setAdding(false); onClearPending?.() }}
          />
        )}

        {visible.length === 0 && !adding ? (
          <div className="comments-empty">
            <p style={{ fontSize: '0.82rem', color: 'var(--stone)', fontStyle: 'italic', fontFamily: 'var(--font-serif)', lineHeight: 1.7 }}>
              No notes yet.<br />
              Highlight text in the editor to add a note, or use the button below.
            </p>
          </div>
        ) : (
          visible.map(comment => (
            <CommentCard
              key={comment.id}
              comment={comment}
              onEdit={handleEdit}
              onResolve={handleResolve}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {/* Add note button */}
      {!adding && (
        <div className="comments-panel-footer">
          <button className="b-col-add-btn" style={{ width: '100%' }} onClick={() => setAdding(true)}>
            + Add note
          </button>
        </div>
      )}
    </div>
  )
}
